/**
 * A recording's audio and an image sit in the vault beside the note that
 * carries them, named like it: the note's own name with the media's extension,
 * or with six characters of the message id added for a second file of the same
 * extension. The app names them (VaultMedia); this only finds them.
 */
const TYPES: Record<string, string> = {
  m4a: 'audio/mp4',
  wav: 'audio/wav',
  aac: 'audio/aac',
  mp3: 'audio/mpeg',
  ogg: 'audio/ogg',
  opus: 'audio/ogg',
  flac: 'audio/flac',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
};

/** The media type a vault path plays as, or null for anything that is not media. */
export function mediaType(path: string): string | null {
  const name = path.slice(path.lastIndexOf('/') + 1);
  const dot = name.lastIndexOf('.');
  return dot < 0 ? null : (TYPES[name.slice(dot + 1).toLowerCase()] ?? null);
}

/** [notePath] without its extension — the prefix every file beside it shares. */
export function stemOf(notePath: string): string {
  const dot = notePath.lastIndexOf('.');
  return dot > notePath.lastIndexOf('/') ? notePath.slice(0, dot) : notePath;
}

/** The media files among [paths] that sit beside [notePath], the one under
 *  the note's own name first. The exporter tells two notes of one title apart
 *  the way a second file is named, so a suffixed file whose name is itself a
 *  note in [paths] is that note's own. */
export function mediaBeside(paths: string[], notePath: string): string[] {
  const stem = stemOf(notePath);
  const notes = new Set(paths.filter((path) => path.endsWith('.md')));
  return paths
    .filter((path) => {
      if (!path.startsWith(stem) || mediaType(path) === null) return false;
      const between = path.slice(stem.length, path.lastIndexOf('.'));
      if (between === '') return true;
      return /^ \([A-Za-z0-9]{6}\)$/.test(between) && !notes.has(`${stem}${between}.md`);
    })
    .sort((a, b) => a.length - b.length || a.localeCompare(b));
}

/** The recording's own audio beside [notePath], if it has reached the vault. */
export function audioBeside(paths: string[], notePath: string): string | null {
  return mediaBeside(paths, notePath).find((path) => mediaType(path)?.startsWith('audio/')) ?? null;
}
